import type { EditorHost } from '@blocksuite/block-std';
import type {
  AffineAIPanelWidgetConfig,
  MindmapStyle,
} from '@blocksuite/blocks';
import { markdownToMindmap, MiniMindmapPreview } from '@blocksuite/blocks';
import { noop } from '@blocksuite/global/utils';
import { nothing } from 'lit';
import { z } from 'zod';

const TIMESTAMP_PATTERN = /\s*\[?\d{1,2}:\d{2}(?::\d{2})?\]?\s*/g;

function cleanNodeTimestamps(mindmapText: string): string {
  return mindmapText
    .split('\n')
    .map(line => line.replace(TIMESTAMP_PATTERN, '').trimEnd())
    .join('\n');
}

import { getAIPanel } from '../utils/edgeless';
noop(MiniMindmapPreview);

export const createMindmapDataProcessor: (
  host: EditorHost,
  ctx: {
    get: () => Record<string, unknown>;
    set: (data: Record<string, unknown>) => void;
  },
  style?: MindmapStyle
) => AffineAIPanelWidgetConfig['answerRenderer'] = (host, ctx, style) => {
  const MindmapMetadataSchema = z.object({
    nodeText: z.string(),
    path: z.array(z.number()),
    timestamp: z.string().nullable(),
    category: z.string().nullable(),
  }).passthrough();
  const BrainstormResultSchema = z.object({
    mindmap: z.string(),
    metadata: z.array(MindmapMetadataSchema),
  }).passthrough();

  return (answer, state) => {
    if (state !== 'finished') {
      const panel = getAIPanel(host);
      if (!panel) return nothing;
      panel.generatingElement?.updateLoadingProgress?.(2);
      return nothing;
    }

    let finalMindmapText = '';
    let finalMetadata: unknown = undefined;

    const resultStartIdx = answer.indexOf('__WORKFLOW_RESULT__');
    const resultEndIdx = answer.indexOf('__END_WORKFLOW_RESULT__');
    if (resultStartIdx !== -1 && resultEndIdx !== -1) {
      const resultSection = answer
        .substring(resultStartIdx + '__WORKFLOW_RESULT__'.length, resultEndIdx)
        .trim();
      try {
        const parsed = JSON.parse(resultSection);
        const check = BrainstormResultSchema.safeParse(parsed);
        if (check.success) {
          finalMindmapText = cleanNodeTimestamps(check.data.mindmap);
          finalMetadata = check.data.metadata as unknown;
        } else {
          console.warn('[mindmap] Schema validation failed:', check.error.issues);
          if (parsed.mindmap) finalMindmapText = cleanNodeTimestamps(parsed.mindmap);
          if (parsed.metadata) finalMetadata = parsed.metadata;
        }
      } catch (err) {
        console.warn('[mindmap] JSON parse error:', err);
      }
    }

    const mindmap = markdownToMindmap(finalMindmapText, host.doc);
    ctx.set({
      node: mindmap,
      answer: finalMindmapText,
      metadata: finalMetadata as any,
      style,
      host,
      templateShow: style === undefined,
      isStreaming: false,
    });

    return nothing;
  };
};

